import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://dash.zorbastays.com"}/web-backend`;

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const calcNights = (checkin, checkout) => {
  if (!checkin || !checkout) return 0;
  const diff = new Date(checkout) - new Date(checkin);
  return Math.max(0, Math.round(diff / (1000 * 60 * 60 * 24)));
};

const Divider = () => (
  <hr
    style={{
      height: 3,
      border: "none",
      backgroundColor: "#1E2827",
      margin: "32px 0",
    }}
  />
);

const SectionTitle = ({ children }) => (
  <h2
    style={{
      fontSize: 18,
      fontWeight: "bold",
      margin: "0 0 20px 0",
      letterSpacing: 1,
      color: "#111",
    }}
  >
    {children}
  </h2>
);

const DetailRow = ({ label, value, subText }) => (
  <>
    <tr>
      <td
        style={{
          padding: "6px 0",
          fontWeight: "bold",
          width: "50%",
          fontSize: 14,
          color: "#111",
        }}
      >
        {label}
      </td>
      <td
        style={{
          padding: "6px 0",
          width: "50%",
          textAlign: "right",
          fontSize: 14,
          color: "#111",
        }}
      >
        {value}
      </td>
    </tr>
    {subText && (
      <tr>
        <td
          colSpan={2}
          style={{ padding: "0 0 6px 0", fontSize: 12, color: "#555" }}
        >
          {subText}
        </td>
      </tr>
    )}
  </>
);

const PreWebsiteLeadPDF = ({ onClose, onPrint }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const autoprint = searchParams.get("autoprint") === "1";

  useEffect(() => {
    if (!loading && lead && autoprint) {
      const timer = setTimeout(() => {
        window.print();
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [loading, lead, autoprint]);

  useEffect(() => {
    if (!autoprint) return;
    const handleAfterPrint = () => {
      window.close();
    };
    window.addEventListener("afterprint", handleAfterPrint);
    return () => window.removeEventListener("afterprint", handleAfterPrint);
  }, [autoprint]);

  useEffect(() => {
    if (!id) return;

    const fetchLead = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API}/processed-website-leads/${id}/pdf`);
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        const data = await res.json();
        setLead(data);
      } catch (err) {
        console.error("Failed to fetch lead:", err);
        setError("Could not load lead details.");
      } finally {
        setLoading(false);
      }
    };

    fetchLead();
  }, [id]);

  const handlePrint = () => {
    if (onPrint) return onPrint();
    window.print();
  };

  const handleClose = () => {
    if (onClose) return onClose();
    navigate(-1);
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#e7e6e2",
          fontFamily: "Arial, Helvetica, sans-serif",
        }}
      >
        Loading lead details…
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
          backgroundColor: "#e7e6e2",
          fontFamily: "Arial, Helvetica, sans-serif",
        }}
      >
        <p>{error || "Lead not found."}</p>
        <button
          onClick={handleClose}
          style={{
            padding: "8px 20px",
            backgroundColor: "#1E2827",
            color: "#e7e6e2",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
          }}
        >
          ← Go Back
        </button>
      </div>
    );
  }

  const nights = calcNights(lead.check_in, lead.check_out);

  const pageStyle = {
    margin: 0,
    padding: 0,
    backgroundColor: "#e7e6e2",
    fontFamily: "Arial, Helvetica, sans-serif",
    minHeight: "100vh",
  };

  const cardStyle = {
    maxWidth: 860,
    margin: "0 auto",
    backgroundColor: "#e7e6e2",
    padding: "40px 32px",
    color: "#111",
  };

  return (
    <>
      <style>{`
@media print {
  html, body {
    background: #e7e6e2 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .invoice {
    background: #e7e6e2 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .no-print { display: none !important; }
  .page-break-before { page-break-before: always; break-before: page; }
  .avoid-break { page-break-inside: avoid; break-inside: avoid; }
}
`}</style>
      <div style={pageStyle}>
        {/* Action bar */}
        <div
          className="no-print"
          style={{
            position: "sticky",
            top: 0,
            zIndex: 100,
            backgroundColor: "#1E2827",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 24px",
            gap: 12,
          }}
        >
          <span style={{ color: "#e7e6e2", fontWeight: "bold", fontSize: 15 }}>
            Pre-Booking Document — {lead.firstname || lead.customer_name}{" "}
            {lead.lastname}
          </span>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={handlePrint}
              style={{
                padding: "8px 20px",
                backgroundColor: "#e7e6e2",
                color: "#1E2827",
                border: "none",
                borderRadius: 6,
                fontWeight: "bold",
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              🖨 Print / Save PDF
            </button>
            <button
              onClick={handleClose}
              style={{
                padding: "8px 16px",
                backgroundColor: "transparent",
                color: "#e7e6e2",
                border: "1px solid #e7e6e2",
                borderRadius: 6,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              ✕ Close
            </button>
          </div>
        </div>

        {/* Document Body */}
        <div style={cardStyle}>
          <table
            width="100%"
            cellPadding={0}
            cellSpacing={0}
            style={{ marginBottom: 8 }}
          >
            <tbody>
              <tr>
                <td style={{ width: "70%", verticalAlign: "top" }}>
                  <p
                    style={{
                      fontSize: 16,
                      margin: "40px 0 0 0",
                      color: "#111",
                    }}
                  >
                    Dear{" "}
                    <strong>
                      {lead.firstname || lead.customer_name} {lead.lastname}
                    </strong>
                    ,
                  </p>
                  <p style={{ fontSize: 16, margin: "12px 0 0 0" }}>
                    Thank you for considering Zorba Stays for your upcoming
                    visit.
                  </p>
                </td>
                <td
                  style={{
                    width: "30%",
                    textAlign: "right",
                    verticalAlign: "top",
                  }}
                >
                  <img
                    src="https://zorbastays.com/android-chrome-512x512.png"
                    alt="Zorba Stays"
                    width={120}
                    style={{ display: "inline-block" }}
                  />
                </td>
              </tr>
            </tbody>
          </table>

          <div className="avoid-break">
            <p style={{ fontSize: 15, margin: "20px 0" }}>
              We are pleased to share the details for your requested stay:
            </p>

            <p style={{ fontSize: 15, margin: "0 0 20px 0", maxWidth: 720 }}>
              To confirm your reservation, we require a <strong>60%</strong>{" "}
              advance payment; the <strong>remaining 40%</strong> + the security
              deposit (if applicable) is to be completed before check-in.
            </p>

            <p style={{ fontSize: 15, margin: "0 0 20px 0" }}>
              As villa availability remains dynamic, we are unable to block or
              guarantee dates without payment confirmation. We therefore
              recommend completing the advance payment at the earliest should
              you wish to secure the property.
            </p>

            <p style={{ fontSize: 15, margin: "0 0 40px 0" }}>
              Kindly review the stay details, payment terms, cancellation
              policy, and house rules attached below. We would be delighted to
              host you and assist with any special arrangements for your stay.
            </p>

            <SectionTitle>STAY DETAILS</SectionTitle>
            <table
              width="100%"
              cellPadding={0}
              cellSpacing={0}
              style={{
                marginBottom: 50,
                borderCollapse: "collapse",
                backgroundColor: "#e7e6e2",
              }}
            >
              <tbody>
                <DetailRow label="PROPERTY:" value={lead.property || "N/A"} />
                <DetailRow
                  label="LOCATION:"
                  value={lead.destination || "N/A"}
                />
                <DetailRow
                  label="CHECK-IN:"
                  value={lead.check_in ? formatDate(lead.check_in) : "N/A"}
                />
                <DetailRow
                  label="CHECK-OUT:"
                  value={lead.check_out ? formatDate(lead.check_out) : "N/A"}
                />
                <DetailRow
                  label="CHECK-IN TIME:"
                  value={lead.checkin_time || "N/A"}
                />
                <DetailRow
                  label="CHECK-OUT TIME:"
                  value={lead.checkout_time || "N/A"}
                />
                <DetailRow
                  label="NUMBER OF GUESTS:"
                  value={lead.guests || "N/A"}
                />
                <DetailRow label="BEDROOMS:" value={lead.bedrooms || "N/A"} />
                <DetailRow
                  label="INCLUSION:"
                  value={lead.inclusions || "N/A"}
                />
              </tbody>
            </table>
          </div>

          <div className="page-break-before">
            <Divider />
            <SectionTitle>STAY PRICING</SectionTitle>
            <table
              width="100%"
              cellPadding={0}
              cellSpacing={0}
              style={{
                fontSize: 14,
                marginBottom: 20,
                borderCollapse: "collapse",
              }}
            >
              <tbody>
                <DetailRow
                  label="NIGHTLY RATE:"
                  value={`₹${lead.proprice || "N/A"}`}
                />
                <DetailRow
                  label="NUMBER OF NIGHTS:"
                  value={`${nights} Night${nights !== 1 ? "s" : ""}`}
                />
                <tr>
                  <td style={{ padding: "6px 0", fontSize: 14 }}>
                    REFUNDABLE SECURITY DEPOSIT:
                  </td>
                  <td
                    style={{
                      padding: "6px 0",
                      fontSize: 14,
                      textAlign: "right",
                    }}
                  >
                    ₹{lead.secdep || "0.00"}
                  </td>
                </tr>
                <tr>
                  <td
                    colSpan={2}
                    style={{
                      padding: "0 0 8px 0",
                      fontSize: 12,
                      color: "#555",
                    }}
                  >
                    (To be paid at check-in &amp; fully refundable at check-out,
                    subject to any damage to the property)
                  </td>
                </tr>
                <DetailRow
                  label="ADVANCE AMOUNT:"
                  value={`₹${lead.advpay || "N/A"}`}
                />
                <DetailRow
                  label="BALANCE DUE:"
                  value={`₹${lead.balpay || "N/A"}`}
                />
              </tbody>
            </table>

            <p
              style={{
                fontSize: 16,
                fontWeight: "bold",
                textAlign: "right",
                margin: "0 0 50px 0",
              }}
            >
              TOTAL PAYABLE: ₹{lead.totpay || "N/A"}
            </p>
          </div>

          <Divider />

          <div className="avoid-break">
            <SectionTitle>CANCELLATION POLICY</SectionTitle>
            <ul
              style={{
                fontSize: 15,
                margin: "0 0 40px 20px",
                padding: 0,
                lineHeight: 1.7,
              }}
            >
              <li style={{ marginBottom: 16 }}>
                <span className="text-xl mr-2">•</span> Advance would be
                refundable if the booking is cancelled 30 days before the
                arrival date. The advance would not be refundable if the booking
                is cancelled within the 30-day period from the arrival date.
              </li>
              <li>
                <span className="text-xl mr-2">•</span> In case of any lockdown
                or government restrictions on travel, the initial payment would
                be fully refundable. Otherwise, refunds are not available unless
                we manage to resell the dates.
              </li>
            </ul>
          </div>

          <div className="page-break-before">
            <Divider />
            <table
              width="100%"
              cellPadding={0}
              cellSpacing={0}
              style={{ borderCollapse: "collapse", marginTop: 20 }}
            >
              <tbody>
                <tr>
                  <td style={{ width: "60%", verticalAlign: "top" }}>
                    <SectionTitle>PAYMENT INFO</SectionTitle>
                    {[
                      ["Bank", "HDFC Bank"],
                      ["A/C No.", "50200077506738"],
                      ["A/C Name", "ZORBA STAYS"],
                      ["IFSC", "HDFC0000101"],
                      ["Branch", "RAJKOT - YAGNIK ROAD - GUJARAT"],
                      ["Account Type", "CURRENT"],
                    ].map(([label, val]) => (
                      <p
                        key={label}
                        style={{
                          margin: "0 0 14px 0",
                          fontSize: 14,
                          color: "#111",
                        }}
                      >
                        <strong>{label}:</strong> {val}
                      </p>
                    ))}
                  </td>

                  <td
                    style={{
                      width: "40%",
                      verticalAlign: "top",
                      textAlign: "right",
                    }}
                  >
                    <SectionTitle>SCAN &amp; PAY</SectionTitle>
                    <img
                      src="https://imagedelivery.net/1KyJN6DqRSgEbKUMt_Oskw/3d7e03f8-1d5f-4377-60bc-161fe96edc00/public"
                      alt="QR Code"
                      style={{
                        width: 180,
                        height: 180,
                        border: "1px solid #ddd",
                        display: "inline-block",
                      }}
                    />
                    <p style={{ margin: "14px 0 6px 0", fontSize: 14 }}>
                      <strong>Name:</strong> ZORBA STAYS
                    </p>
                    <p style={{ margin: 0, fontSize: 14 }}>
                      <strong>UPI ID:</strong> 8866262368@hdfcbank
                    </p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p style={{ fontSize: 15, margin: "40px 0 32px 0" }}>
            Warm regards,
            <br />
            <strong>Team Zorba Stays</strong>
          </p>

          <div style={{ marginBottom: 16, textAlign: "center" }}>
            {[
              "Contact Us",
              "Experiences",
              "destinations",
              "Travel Stories",
            ].map((link, i) => (
              <a
                key={i}
                href="#"
                style={{
                  display: "inline-block",
                  marginRight: 32,
                  fontSize: 12,
                  color: "#000",
                  textDecoration: "none",
                }}
              >
                {link}
              </a>
            ))}
          </div>
          <p
            style={{ fontSize: 12, color: "#777", lineHeight: 1.6, margin: 0 }}
          >
            Zorba Stays is registered in India, Registered company number: 90167
            72761, Registered address: 308 - Business Centrum, Amin Marg,
            Rajkot, India
          </p>
        </div>
      </div>
    </>
  );
};

export default PreWebsiteLeadPDF;
